import { useState } from "react";

import type React from "react";
import { Building2, ChevronRight, ChevronsUpDown, Lock } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { BrandMark } from "@/components/BrandMark";
import { buildCentralChatHref, currentRoute } from "@/components/CentralChat/navigation";

import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Separator } from "@/components/ui/separator";
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupContent,
	SidebarGroupLabel,
	SidebarHeader,
	SidebarInset,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
	SidebarProvider,
	SidebarRail,
	SidebarTrigger,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { CentralChatHeaderLauncher } from "@/components/CentralChat/CentralChatHeaderLauncher";
import { CentralChatSurface } from "@/components/CentralChat/CentralChatSurface";
import { CentralChatProvider } from "@/components/CentralChat/centralChatRuntime";
import { CentralChatBridgeRegister } from "@/layouts/CentralChatBridgeRegister";
import { ThemeToggle } from "@/components/theme/ThemeToggle";
import {
	buildNavigationFlags,
	filterNavigation,
	isNavigationChildGroup,
	isNavigationChildLink,
	NAV_BADGE_METADATA,
	navigationItems,
	routeTitles,
	type NavigationChildItem,
	type NavigationBadgeKey,
	type NavigationItem,
} from "./navigation";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { AdsViewControls } from "@/components/google-ads/AdsViewControls";
import { ViewAsControl } from "@/components/ViewAsControl";
import ContentCreationOptions from "../components/ContentCreationOptions";
import { SearchCommand } from "../components/SearchCommand";
import { NotificationCenter } from "../components/NotificationCenter";
import UserMenu from "../components/UserMenu";
import WorkspaceSwitcher from "../components/WorkspaceSwitcher";
import { shouldHideWorkspaceSwitcher } from "./workspaceSwitcherRoutes";
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "@/components/ui/sheet";
import { blogCreationService } from "../services/blogCreationService";
import {
	getOperatorQueueCounts,
	operatorQueueCountsQueryKey,
} from "../services/googleAdsOperatorQueueService";
import { gbpRecommendationsService } from "../services/gbpRecommendationsService";
import { getPmaxImageReadiness } from "../services/googleAdsCampaignsService";
import { getWcLeads } from "../services/whatconvertsService";
import { getLatestAccountAudit } from "../services/googleAdsAccountAuditService";
import { listWeeklyApprovals } from "../services/googleAdsWeeklyService";
import { plannerService } from "../services/plannerService";
import { ticketsService } from "../services/ticketsService";
import { useTenantStore } from "../stores/useTenantStore";
import { useWorkspaceStore } from "../stores/useWorkspaceStore";

type NavigationBadgeCounts = Partial<Record<NavigationBadgeKey, number>>;

function NavBadge({
	count,
	badgeKey,
}: {
	count?: number;
	badgeKey?: NavigationBadgeKey;
}) {
	if (!count || count <= 0) return null;
	// Fall back to "pending" only if a caller passes a badgeKey we don't have
	// metadata for. Every declared key has metadata.
	const meta = badgeKey ? NAV_BADGE_METADATA[badgeKey] : undefined;
	const describe = meta ? meta.describe(count) : `${count} pending`;
	// ⚠ Chip renders count ONLY. An earlier version rendered "{count} {label}"
	// inside the chip ("45 to review") so operators wouldn't see a bare
	// number and wonder what it meant. In the narrow sidebar with a long
	// title like "Search Terms & Negatives" that pushed total row width past
	// the container and clipped the chip to "4" (looked like a data bug —
	// #2688, #2695, #2752 chased this). Fix: chip stays count-only for
	// layout, and the descriptor lives in the tooltip (Radix hover) + the
	// native `title` (mobile / long-press / keyboard focus) + the
	// `aria-label` (screen readers). Three redundant surfaces so the label
	// is never a mystery to any user without also making the chip wide.
	// ⚠ Element MUST be a <div>, not a <span>: SidebarMenuSubButton applies
	// `[&>span:last-child]:truncate` and would clip the count itself as
	// soon as it hits two digits.
	const chip = (
		<div
			className="ml-auto shrink-0 whitespace-nowrap rounded-md border border-coral/30 bg-coral-light px-1.5 py-0.5 text-[10px] font-semibold leading-none text-coral-dark tabular-nums"
			aria-label={describe}
			title={describe}
		>
			{count}
		</div>
	);
	if (!meta) return chip;
	return (
		<TooltipProvider delayDuration={200}>
			<Tooltip>
				<TooltipTrigger asChild>{chip}</TooltipTrigger>
				<TooltipContent side="right" className="max-w-xs">
					{meta.describe(count)}
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	);
}

// Navigation structure (types, registry, gating) lives in ./navigation —
// this layout only renders it and computes active state from the location.
function isNavigationChildActive(
	item: NavigationChildItem,
	location: ReturnType<typeof useLocation>,
) {
	if (isNavigationChildLink(item)) return isNavUrlActive(item.url, location, item.match);
	return item.items.some((child) => isNavUrlActive(child.url, location, child.match));
}

// Handle navigation with support for opening in new tabs (Ctrl+Click, Cmd+Click, middle-click)
function handleNavClick(
	e: React.MouseEvent<HTMLAnchorElement>,
	url: string,
	navigate: ReturnType<typeof useNavigate>,
) {
	// Allow default behavior for new tab actions
	if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) {
		return; // Let the browser handle it (opens in new tab)
	}
	// Otherwise, use client-side navigation
	e.preventDefault();
	navigate(url);
}

function buildCentralChatUrl(location: ReturnType<typeof useLocation>) {
	if (location.pathname === "/chat") return "/chat";
	return buildCentralChatHref({ route: currentRoute(location.pathname, location.search) });
}

// A `match` prefix ending in "/" uses plain startsWith; otherwise it matches
// the exact pathname or any sub-path (prefix + "/...").
function matchesNavPrefix(pathname: string, prefix: string) {
	if (prefix.endsWith("/")) return pathname.startsWith(prefix);
	return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

function isNavUrlActive(
	url: string,
	location: ReturnType<typeof useLocation>,
	match?: string[],
) {
	if (match?.some((prefix) => matchesNavPrefix(location.pathname, prefix))) {
		return true;
	}

	const [targetPath, targetQuery = ""] = url.split("?");
	if (location.pathname !== targetPath) return false;
	if (!targetQuery) return true;

	const targetParams = new URLSearchParams(targetQuery);
	const currentParams = new URLSearchParams(location.search);
	for (const [key, value] of targetParams.entries()) {
		const currentValue = currentParams.get(key);
		if (currentValue === value) continue;
		if (key === "platform" && value === "google-ads" && !currentValue) continue;
		if (key === "tab" && value === "keyword-research" && !currentValue) continue;
		if (
			key === "campaign_tab" &&
			value === "create" &&
			(!currentValue || ["approvals", "logs", "history"].includes(currentValue))
		) continue;
		return false;
	}
	return true;
}

// Collapsible navigation item component
function NavItem({
	item,
	isActive,
	badges,
}: {
	item: NavigationItem;
	isActive: boolean;
	badges?: NavigationBadgeCounts;
}) {
	const navigate = useNavigate();
	const location = useLocation();

	// Check if any child is active
	const hasActiveChild = item.items?.some((child) =>
		isNavigationChildActive(child, location),
	);
	const itemUrl = item.url === "/chat" ? buildCentralChatUrl(location) : item.url;

	// For items without children
	if (!item.items) {
		const badgeKey = item.badgeKey;
		return (
			<SidebarMenuItem>
				<SidebarMenuButton asChild isActive={isActive} tooltip={item.title}>
					<a
						href={itemUrl!}
						onClick={(e) => handleNavClick(e, itemUrl!, navigate)}
						// The sidebar truncates any label that outgrows its width
						// (sidebar.tsx: [&>span:last-child]:truncate), and the
						// SidebarMenuButton `tooltip` prop only surfaces when the
						// sidebar is COLLAPSED to icons. Expanded, a long entry like
						// "Google Business Profile" rendered as "Google Business Pr…"
						// with no way to read it. A native title covers that case.
						title={item.title}
					>
						<item.icon className="size-4 shrink-0" />
						{/* When a badge is present the title needs explicit
						    `min-w-0 flex-1 truncate` — same rationale as the
						    sub-item render below (NavBadge is a <div> so the
						    sidebar's default title-truncation rule doesn't fire). */}
						<span className={badgeKey ? "min-w-0 flex-1 truncate" : undefined}>
							{item.title}
						</span>
						{badgeKey ? (
							<NavBadge
								badgeKey={badgeKey}
								count={badges?.[badgeKey]}
							/>
						) : null}
					</a>
				</SidebarMenuButton>
			</SidebarMenuItem>
		);
	}

	// For items with children (collapsible)
	return (
		<Collapsible
			asChild
			defaultOpen={hasActiveChild}
			className="group/collapsible"
		>
			<SidebarMenuItem>
				<CollapsibleTrigger asChild>
					<SidebarMenuButton
						tooltip={item.title}
						isActive={hasActiveChild}
						title={item.title}
					>
						<item.icon className="size-4" />
						<span>{item.title}</span>
						<ChevronRight className="ml-auto size-4 transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-data-[state=open]/collapsible:rotate-90" />
					</SidebarMenuButton>
				</CollapsibleTrigger>
				<CollapsibleContent>
					<SidebarMenuSub className={item.wideChildren ? "mr-0 pr-1" : undefined}>
						{item.items.map((subItem, index) => {
							if (isNavigationChildGroup(subItem)) {
								const sectionIsActive = isNavigationChildActive(subItem, location);

								return (
									<Collapsible
										asChild
										defaultOpen={sectionIsActive}
										className="group/ads-nav-section"
										key={`${subItem.title}-${index}`}
									>
										<SidebarMenuSubItem>
											<CollapsibleTrigger asChild>
												<SidebarMenuSubButton
													asChild
													isActive={sectionIsActive}
													className="font-semibold text-muted-foreground"
												>
													<button type="button" title={subItem.title}>
														<ChevronRight className="size-3.5 transition-transform duration-200 group-data-[state=open]/ads-nav-section:rotate-90" />
														<span>{subItem.title}</span>
													</button>
												</SidebarMenuSubButton>
											</CollapsibleTrigger>
											<CollapsibleContent>
												<ul className="ml-3 mt-1 flex flex-col gap-1 border-l border-sidebar-border pl-2">
													{subItem.items.map((child) => (
														<SidebarMenuSubItem key={child.url}>
															<SidebarMenuSubButton
																asChild
																isActive={isNavUrlActive(child.url, location, child.match)}
															>
																<a
																	href={child.url}
																	onClick={(e) => handleNavClick(e, child.url, navigate)}
																	className="cursor-pointer w-full flex items-center gap-2"
																	title={child.title}
																>
																	{child.icon && <child.icon className={cn("size-3.5 shrink-0", child.color)} />}
																	{/* Title MUST have `min-w-0 truncate` when a NavBadge sits
																	    beside it. The sidebar primitive's default
																	    `[&>span:last-child]:truncate` only fires when the LAST
																	    child is a span; NavBadge is a <div> (see rationale on
																	    the NavBadge chip), so the title span isn't caught by
																	    the default rule. Without an explicit truncate the
																	    long "Search Terms & Negatives" title + chip overflows
																	    the row. */}
																	<span className={cn("min-w-0 flex-1 truncate", child.color)}>{child.title}</span>
																	<NavBadge
																		badgeKey={child.badgeKey}
																		count={child.badgeKey ? badges?.[child.badgeKey] : undefined}
																	/>
																</a>
															</SidebarMenuSubButton>
														</SidebarMenuSubItem>
													))}
												</ul>
											</CollapsibleContent>
										</SidebarMenuSubItem>
									</Collapsible>
								);
							}

							if (!isNavigationChildLink(subItem)) {
								return null;
							}

							return (
								<SidebarMenuSubItem key={subItem.url}>
									<SidebarMenuSubButton
										asChild
										isActive={isNavUrlActive(subItem.url, location, subItem.match)}
									>
										<a
											href={subItem.url}
											onClick={(e) => handleNavClick(e, subItem.url, navigate)}
											className="cursor-pointer w-full flex items-center gap-2"
											title={
												subItem.locked
													? `${subItem.title} — locked, not available yet`
													: subItem.title
											}
										>
											{subItem.icon && <subItem.icon className={cn("size-3.5 shrink-0", subItem.color)} />}
											{/* See rationale on the group-child span above: explicit
											    `min-w-0 flex-1 truncate` needed because NavBadge (a <div>)
											    prevents the sidebar's default title-truncation rule from
											    firing on the title span. */}
											<span className={cn("min-w-0 flex-1 truncate", subItem.color)}>{subItem.title}</span>
											{subItem.locked ? (
												<Lock className="ml-auto size-3 shrink-0 text-muted-foreground" />
											) : (
												<NavBadge
													badgeKey={subItem.badgeKey}
													count={subItem.badgeKey ? badges?.[subItem.badgeKey] : undefined}
												/>
											)}
										</a>
									</SidebarMenuSubButton>
								</SidebarMenuSubItem>
							);
						})}
					</SidebarMenuSub>
				</CollapsibleContent>
			</SidebarMenuItem>
		</Collapsible>
	);
}

const ModernMainLayout: React.FC = () => {
	const location = useLocation();
	const { userProfile, currentWorkspace } = useWorkspaceStore();
	const [mobileControlsOpen, setMobileControlsOpen] = useState(false);
	const currentTenant = useTenantStore((state) => state.currentTenant);
	const navigationFlags = buildNavigationFlags(userProfile, currentWorkspace);
	const isAdsToolsRoute = location.pathname.startsWith("/ads-tools");
	// Where a workspace picker makes no sense (Clients, the fleet Client Dashboard (CORE-D-01.c, Jacob
	// 2026-09-18) and the Admin Panel / every /admin/* page): one list, see workspaceSwitcherRoutes.ts.
	const hideWorkspaceSwitcher = shouldHideWorkspaceSwitcher(location.pathname);
	const filteredNavigationItems = filterNavigation(navigationItems, navigationFlags);
	const humanQaPendingQuery = useQuery({
		queryKey: ["human-qa", "nav-pending-count", currentTenant],
		queryFn: () =>
			blogCreationService.listHumanQaQueue({
				tenantId: currentTenant,
				status: "pending",
			}),
		enabled: navigationFlags.canAdsTools && Boolean(currentTenant),
		staleTime: 60_000,
		refetchInterval: 60_000,
	});
	const operatorQueueQuery = useQuery({
		queryKey: operatorQueueCountsQueryKey(currentTenant ?? ""),
		queryFn: () => getOperatorQueueCounts(currentTenant as string),
		enabled: navigationFlags.canAdsTools && Boolean(currentTenant),
		staleTime: 60_000,
		refetchInterval: 60_000,
	});
	// New nav-badge queries — one per surface. Every one:
	//  1. Guards on `Boolean(currentTenant)` so we don't POST /null.
	//  2. Uses `staleTime === refetchInterval` so React Query polls exactly at
	//     that cadence and doesn't refetch on every mount within the window.
	//  3. Silently returns 0 on missing/error data — NavBadge hides itself
	//     when count is 0, so a broken endpoint never blocks the app.
	//  4. Polls at a cadence appropriate to how fast the count moves. Cheap +
	//     fast-moving surfaces poll every 60s; heavier / slower-moving ones
	//     every 2-5 min so we don't hammer the backend.
	const gbpQuery = useQuery({
		queryKey: ["nav-badge", "gbp-recommendations", currentTenant],
		queryFn: () => gbpRecommendationsService.get(currentTenant as string),
		enabled: Boolean(currentTenant),
		staleTime: 300_000,
		refetchInterval: 300_000,
	});
	const pmaxReadinessQuery = useQuery({
		queryKey: ["nav-badge", "pmax-readiness", currentTenant],
		queryFn: () => getPmaxImageReadiness(currentTenant as string),
		enabled: navigationFlags.canAdsTools && Boolean(currentTenant),
		staleTime: 120_000,
		refetchInterval: 120_000,
	});
	const whatConvertsQuery = useQuery({
		queryKey: ["nav-badge", "whatconverts-pending", currentTenant],
		queryFn: () =>
			getWcLeads({
				tenantId: currentTenant as string,
				quotable: "pending",
				days: 90,
			}),
		enabled: navigationFlags.canAdsTools && Boolean(currentTenant),
		staleTime: 120_000,
		refetchInterval: 120_000,
	});
	const accountAuditQuery = useQuery({
		queryKey: ["nav-badge", "account-audit-latest", currentTenant],
		queryFn: () => getLatestAccountAudit(currentTenant as string),
		enabled: navigationFlags.canAdsTools && Boolean(currentTenant),
		staleTime: 300_000,
		refetchInterval: 300_000,
	});
	const weeklyApprovalsQuery = useQuery({
		queryKey: ["nav-badge", "weekly-approvals", currentTenant],
		queryFn: () => listWeeklyApprovals(currentTenant as string, "admin"),
		enabled: navigationFlags.canAdsTools && Boolean(currentTenant),
		staleTime: 60_000,
		refetchInterval: 60_000,
	});
	// Planner approvals are STAFF-scoped (not tenant-scoped) — one global
	// pending queue across all clients. Gate on canAdsTools which mirrors
	// the `agileco` nav gate on the Planner group.
	const plannerApprovalsQuery = useQuery({
		queryKey: ["nav-badge", "planner-approvals-pending"],
		queryFn: () => plannerService.approvals("pending"),
		enabled: navigationFlags.canAdsTools,
		staleTime: 120_000,
		refetchInterval: 120_000,
	});
	// Support tickets — "open" == status != done, assigned to the current
	// user. `listMine` returns cross-tenant when tenantId is omitted; passing
	// currentTenant keeps the count tenant-scoped so switching tenants
	// changes the badge (matches every other badge on this layout).
	const supportTicketsQuery = useQuery({
		queryKey: ["nav-badge", "support-tickets-open", currentTenant],
		queryFn: () => ticketsService.listMine(currentTenant),
		enabled: Boolean(currentTenant),
		staleTime: 60_000,
		refetchInterval: 60_000,
	});

	const navigationBadges: NavigationBadgeCounts = {
		humanQaPending: humanQaPendingQuery.data?.items?.length ?? 0,
		managementEasyYes: operatorQueueQuery.data?.management_easy_yes ?? 0,
		searchTermsEasy: operatorQueueQuery.data?.search_terms_easy ?? undefined,
		gbpPending:
			// Don't badge non-connected / unmapped / unreadable states — the
			// count would be 0 but the operator can't act on it from here.
			gbpQuery.data?.connected &&
			gbpQuery.data?.mapped &&
			!gbpQuery.data?.unreadable &&
			!gbpQuery.data?.analysisMissing
				? gbpQuery.data?.recommendations?.length ?? 0
				: 0,
		imageAssetsMissing:
			// Only badge when PMax is actually enabled — the `missing` list is
			// meaningless for tenants who don't run PMax.
			pmaxReadinessQuery.data?.feature_enabled === false
				? 0
				: pmaxReadinessQuery.data?.missing?.length ?? 0,
		whatConvertsPending: whatConvertsQuery.data?.count ?? 0,
		accountAuditFailed: accountAuditQuery.data?.summary?.failed ?? 0,
		weeklyManagementPending: weeklyApprovalsQuery.data?.length ?? 0,
		plannerApprovalsPending: plannerApprovalsQuery.data?.length ?? 0,
		supportTicketsOpen:
			// listMine's response has an `items` array. Count "open" = not done.
			supportTicketsQuery.data?.items?.filter(
				(t) => t.status !== "done",
			).length ?? 0,
	};

	const pageTitle = routeTitles[location.pathname] || "Dashboard";

	// Editor routes (/editor/:contentType/:contentId) and the central chat
	// (/chat) own their own chrome, so the layout drops its outer gutters and
	// inner card frame for both.
	const isEditorRoute = location.pathname.startsWith("/editor/");
	const isChatRoute = location.pathname === "/chat" || location.pathname.startsWith("/chat?");
	const isFullBleedRoute = isEditorRoute || isChatRoute;

	return (
		<SidebarProvider>
			<CentralChatProvider>
			<CentralChatBridgeRegister>
				<Sidebar collapsible="icon" className="bg-[hsl(240_5%_97%)] border-r border-border/50">
				<SidebarHeader className="border-b border-sidebar-border">
					{/* ⚠ THE LOCKUP CARRIES EVERYTHING NOW. It used to render the
					    name twice — a cropped image beside the same words as
					    text — which is why the illegible tile went unnoticed:
					    the label carried the meaning and nobody read the mark.
					    The "SEO Management" descriptor is gone too: the revamp
					    lockup already says AGILE & CO over POWERED BY CORE, so
					    a third line of chrome repeated what the artwork states
					    and contradicted it (this is Core, and it is more than
					    SEO). */}
					<div className="flex items-center gap-2.5 px-2 py-1 group/logo cursor-pointer">
						<BrandMark
							height={26}
							className="transition-transform duration-200 group-hover/logo:scale-[1.03] group-data-[collapsible=icon]:hidden"
						/>
						<BrandMark
							glyph
							height={26}
							labelled={false}
							className="hidden group-data-[collapsible=icon]:inline-flex"
						/>
					</div>
				</SidebarHeader>

				<SidebarContent className="scrollbar-thin">
					<SidebarGroup>
						<SidebarGroupLabel>Navigation</SidebarGroupLabel>
						<SidebarGroupContent>
							<SidebarMenu>
								{filteredNavigationItems.map((item) => (
									<NavItem
										key={item.title}
										item={item}
										isActive={location.pathname === item.url}
										badges={navigationBadges}
									/>
								))}
							</SidebarMenu>
						</SidebarGroupContent>
					</SidebarGroup>
				</SidebarContent>

				<SidebarFooter className="border-t border-sidebar-border">
					<UserMenu />
				</SidebarFooter>

				<SidebarRail />
			</Sidebar>

			{/* overflow-x-clip, not -hidden: `overflow-x: hidden` forces overflow-y to
			    compute to `auto`, which makes this a scrollport and neutralises any
			    position:sticky inside it (the editor's top chrome). `clip` clips the
			    same box without creating one. */}
			<SidebarInset className="min-w-0 overflow-x-clip bg-[hsl(var(--surface-sunken))]">
				{/* Top Header Bar */}
				<header className="sticky top-0 z-10 flex h-16 items-center gap-4 border-b border-border/40 bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60 px-4">
					<SidebarTrigger className="-ml-1" />
					<Separator orientation="vertical" className="h-4" />

					{/* ⚠ The title yields space before the controls do. Without min-w-0
					    + truncate a long page title pushes the right-hand group off
					    the edge, and the header clips rather than wraps. */}
					{/* ⚠ THE CRUMB ROOT WAS A CONSTANT — the agency's own name, in
					    the one slot a reader scans for hierarchy, asserting a
					    hierarchy that does not exist. Which CLIENT you are in is
					    the fact that matters there, and it already lives in the
					    workspace switcher.

					    ⚠ min-w-0 ALONE MAKES THE TITLE VANISH, NOT TRUNCATE: the
					    toolbar does not shrink, so the h1 computes to width 0 at
					    narrow widths. The floor is what makes truncation
					    happen. */}
					<div className="flex min-w-[7rem] flex-1 items-center">
						<h1 className="truncate font-display text-lg font-semibold tracking-tight">
							{pageTitle}
						</h1>
					</div>

					<div className="flex shrink-0 items-center gap-2 md:gap-3">
							{/* ⛔ AT 390px THESE DID NOT FIT AND WERE SILENTLY CLIPPED.
							    Measured: the workspace picker rendered at x=384..574, six
							    pixels PAST the right edge and 184px beyond it, inside an
							    overflow-x-hidden header — so it was unreachable rather
							    than merely cramped, and nothing on any page loads until a
							    workspace is chosen.

							    Rather than drop controls on small screens, the workspace
							    picker, search and the ads view controls collapse into ONE
							    grouped sheet. The trigger still shows the current
							    workspace, because which client you are looking at is
							    context an operator must never lose. Tablet and up keep the
							    original row. */}
							<div className="hidden min-w-0 md:flex md:items-center md:gap-3">
								<AdsViewControls />
								<ViewAsControl />
								<SearchCommand />
								{!hideWorkspaceSwitcher && <WorkspaceSwitcher />}
							</div>

							{/* ⛔ THE SHEET ITSELF CANNOT LIVE IN THIS HEADER.
							    DialogPortal in this codebase is `({children}) => <>{children}</>`
							    — it does not portal, so the panel stays a DOM child of wherever
							    it is declared. This header sets backdrop-blur-xl, and
							    backdrop-filter creates a CONTAINING BLOCK for fixed-position
							    descendants, so `fixed inset-y-0` resolved against the 64px
							    header instead of the viewport: the panel rendered as a strip
							    across the top. Only the trigger stays here; the panel is
							    rendered below, outside the filtered ancestor. */}
							<button
								type="button"
								onClick={() => setMobileControlsOpen(true)}
								className="flex h-11 max-w-[11rem] items-center gap-2 rounded-md border border-border/50 bg-background px-3 text-sm md:hidden"
								aria-label="Workspace, search and view controls"
							>
								<Building2 className="size-4 shrink-0 text-muted-foreground" />
								<span className="min-w-0 flex-1 truncate text-left font-medium">
									{currentWorkspace?.name ?? currentTenant ?? "Select workspace"}
								</span>
								<ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground" />
							</button>
							{navigationFlags.isElevated && !isAdsToolsRoute && <ContentCreationOptions />}
							<CentralChatHeaderLauncher pathname={location.pathname} />
							<ThemeToggle />
							<NotificationCenter />
					</div>
				</header>

				{/* Main Content */}
				<main
					className={cn(
						"flex-1 w-full min-w-0 overflow-x-clip",
						isFullBleedRoute
							? "p-0 max-w-none flex flex-col min-h-0"
							: "p-4 md:p-6 max-w-[1600px] mx-auto",
					)}
				>
					{isFullBleedRoute ? (
						<Outlet />
					) : (
						<div className="bg-card rounded-xl shadow-sm min-h-[calc(100vh-8rem)] p-6 overflow-x-hidden">
							<Outlet />
						</div>
					)}
				</main>

			{/* Mobile workspace + controls panel. Declared OUTSIDE the header on
			    purpose — see the note on the trigger. */}
			<Sheet open={mobileControlsOpen} onOpenChange={setMobileControlsOpen}>
				<SheetContent
					side="right"
					className="flex w-[min(20rem,calc(100vw-3rem))] flex-col gap-4 overflow-y-auto sm:max-w-none"
				>
					<SheetHeader className="text-left">
						<SheetTitle>Workspace and controls</SheetTitle>
					</SheetHeader>
					{!hideWorkspaceSwitcher && (
						<div className="flex flex-col gap-2">
							<span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
								Workspace
							</span>
							<WorkspaceSwitcher />
						</div>
					)}
					<div className="flex flex-col gap-2">
						<span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
							Search
						</span>
						<SearchCommand />
					</div>
					<div className="flex flex-wrap items-center gap-2">
						<AdsViewControls />
						<ViewAsControl />
					</div>
				</SheetContent>
			</Sheet>
			</SidebarInset>
			{/* Which Emjae surface mounts is the build-time VITE_CHAT_SURFACE choice
			    (CHAT_SURFACE; default = floating bubble). With "rail", ordinary app
			    pages dock the MJ rail as an in-flow sibling of SidebarInset so
			    expanding it narrows the page, and settings keeps the bubble. The
			    editor (its own docked panel) and /chat (its own page) mount nothing
			    in either mode. The rule is chatSurfaceFor in railRoutes.ts. */}
			<CentralChatSurface pathname={location.pathname} />
			</CentralChatBridgeRegister>
			</CentralChatProvider>
		</SidebarProvider>
	);
};

export default ModernMainLayout;
